import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Termos e Condições de Uso e Serviço</h1>
              <p className="text-gray-600">Minha Composição - Última atualização: Janeiro 2025</p>
            </div>
          </div>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="prose prose-lg max-w-none space-y-6">
            
            {/* Section 1 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. DAS PARTES</h2>
              <p className="text-gray-700 mb-4">
                O presente instrumento estabelece os Termos e Condições de Uso e Serviço ("Termos") aplicáveis à utilização da plataforma digital <strong>MINHA COMPOSIÇÃO AUTORAL</strong>, mantida por <strong>MINHA COMPOSIÇÃO AUTORAL</strong>, pessoa jurídica de direito privado, inscrita no CNPJ nº <strong>63.854.898/0001-85</strong>, com sede na Avenida Santos Dumont, nº 6061, Sala 515, Portão, Lauro de Freitas – BA, CEP 42.712-740, e-mail: <strong>sacminhascomposicoes@gmail.com</strong>, doravante denominada <strong>CONTRATADA</strong>.
              </p>
              <p className="text-gray-700 mb-4">
                O <strong>USUÁRIO</strong> ou <strong>CONTRATANTE</strong> é toda pessoa física ou jurídica que acessa, utiliza ou contrata os serviços disponibilizados pela CONTRATADA, declarando:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>ser civilmente capaz e maior de 18 anos, ou representar legalmente um menor de idade;</li>
                <li>possuir plenos direitos sobre as obras que venha a registrar;</li>
                <li>ter lido, compreendido e aceitado integralmente estes Termos.</li>
              </ul>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. DO OBJETO</h2>
              <p className="text-gray-700 mb-4">
                A Minha Composição Autoral é uma plataforma tecnológica que oferece meios digitais de registro, prova e divulgação de obras musicais, permitindo ao USUÁRIO comprovar autoria, data de criação e integridade de suas composições.
              </p>
              <p className="text-gray-700 mb-4">
                Os serviços constituem meios de <strong>prova privada</strong> (art. 107 do Código Civil e art. 6º, II, do Marco Civil da Internet), não substituindo o registro público perante a Biblioteca Nacional / Escritório de Direitos Autorais (EDA) ou quaisquer outros órgãos oficiais.
              </p>
              <p className="text-gray-700 mb-4">
                A CONTRATADA não adquire direitos morais ou patrimoniais sobre as obras registradas, atuando exclusivamente como prestadora de tecnologia de autenticação, certificação e proteção musical.
              </p>
              <p className="text-gray-700 mb-4">
                Além disso, o USUÁRIO poderá autorizar, mediante aceite destes Termos, que a <strong>MINHA COMPOSIÇÃO AUTORAL</strong> o represente, por procuração específica, para efetuar, a cada 60 (sessenta) dias, o <strong>registro coletivo e oficial</strong> de suas obras musicais junto à Biblioteca Nacional, conforme autorizado pela Lei nº 9.610/98 e normas administrativas da BN.
              </p>
              <p className="text-gray-700 mb-4">
                Esse registro é realizado de forma coletiva (coletânea), agrupando as obras registradas na plataforma dentro do período de referência, <strong>sem transferência de titularidade</strong>, apenas para fins administrativos e de reforço jurídico de proteção autoral.
              </p>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. COMO FUNCIONA NA PRÁTICA</h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-2">1. Você Registra Sua Obra</h3>
                <p className="text-gray-700">
                  O USUÁRIO faz upload da letra, áudio ou partitura, e o sistema gera automaticamente o <strong>Certificado Digital de Registro Autoral</strong>, com carimbo de tempo, hash criptográfico (SHA-256) e assinatura eletrônica válida conforme a Lei nº 14.063/2020.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-600 p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-2">2. Obra Fica Pública (Opcional)</h3>
                <p className="text-gray-700">
                  O USUÁRIO pode autorizar a divulgação pública da obra. O sistema gera uma página otimizada para SEO, exibindo a letra completa, o título, o nome do autor e a data do registro, garantindo visibilidade nos mecanismos de busca.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-600 p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-2">3. Disponível Imediatamente</h3>
                <p className="text-gray-700">
                  Após a emissão do certificado, a obra fica disponível para consulta pública instantânea, por meio de link de verificação único e rastreável.
                </p>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-600 p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-2">4. IA Reconhece</h3>
                <p className="text-gray-700">
                  As obras publicadas são indexadas por sistemas de inteligência artificial (como ChatGPT, Gemini, Claude, Perplexity e outros), que passam a reconhecer automaticamente a autoria e os metadados originais, fortalecendo a identificação autoral global.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-2">5. Proteção Ativa</h3>
                <p className="text-gray-700">
                  Caso alguém copie ou divulgue indevidamente um trecho da letra, os mecanismos de busca indicarão que a obra já estava registrada anteriormente na plataforma, assegurando prova de anterioridade e autoria reconhecível.
                </p>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-2">6. Visibilidade e Alcance Global</h3>
                <p className="text-gray-700">
                  As obras registradas ganham divulgação automática e alcance internacional, com indexação otimizada. Exemplo real: Alguém pesquisa um trecho da sua letra no Google → aparece: "Esta letra pertence à música [Título], de autoria de [Seu Nome], registrada em [Data] na plataforma Minha Composição Autoral."
                </p>
              </div>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. DOS SERVIÇOS OFERECIDOS</h2>
              <p className="text-gray-700 mb-4">
                <strong>4.1.</strong> O USUÁRIO poderá cadastrar-se, enviar arquivos e emitir o Certificado Digital de Registro Autoral.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.2.</strong> Modalidades de assinatura:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Assinatura Avançada (Gov.br)</li>
                <li>Assinatura Qualificada (ICP-Brasil)</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>4.3.</strong> Funcionalidades adicionais:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>geração automática de partitura e sincronização letra-áudio;</li>
                <li>gestão de coautoria e percentuais de splits;</li>
                <li>licenciamento digital e cessões com assinatura eletrônica;</li>
                <li>página pública com otimização SEO;</li>
                <li>integração com IA e sistemas de indexação.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>4.4.</strong> O prazo para emissão do certificado é de até 5 (cinco) dias úteis, prorrogável por até 30 (trinta) dias por motivo técnico justificado.
              </p>
              
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl my-6">
                <h3 className="text-xl font-bold mb-3">4.5. Registro Oficial Coletivo na Biblioteca Nacional (BN)</h3>
                <p className="mb-3">
                  A Minha Composição oferece, como <strong>benefício adicional</strong>, o registro oficial coletivo e periódico das obras musicais de seus usuários junto à Biblioteca Nacional, por meio de procuração digital concedida ao aceitar estes Termos.
                </p>
                <p className="font-semibold mb-2">Funcionamento:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>A cada 120 (cento e vinte) dias, a CONTRATADA reunirá as obras registradas no período e realizará o protocolo coletivo de registro ("coletânea de obras musicais") perante a BN, na condição de procuradora dos respectivos autores.</li>
                  <li>O processo seguirá os procedimentos oficiais da BN, com GRU única e anexação das obras em arquivo único (índice + letras + partituras).</li>
                  <li>O comprovante oficial de registro será disponibilizado digitalmente ao USUÁRIO.</li>
                  <li>O registro é complementar ao certificado digital emitido pela plataforma, proporcionando <strong>dupla camada de proteção jurídica até 120 dias gratuito</strong> (prova privada + registro público).</li>
                  <li>O autor permanece titular exclusivo da obra, sem qualquer cessão de direitos.</li>
                  <li>A CONTRATADA atua apenas como representante legal administrativa, sem poderes de exploração comercial.</li>
                </ul>
              </div>
            </div>

            {/* Remaining sections */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. PAGAMENTO</h2>
              <p className="text-gray-700 mb-2"><strong>5.1.</strong> Os valores constam no site oficial e podem variar conforme o tipo de registro.</p>
              <p className="text-gray-700 mb-2"><strong>5.2.</strong> O pagamento pode ser feito por PIX, cartão, boleto ou carteira digital.</p>
              <p className="text-gray-700 mb-2"><strong>5.3.</strong> A falta de pagamento cancela a solicitação de registro.</p>
              <p className="text-gray-700 mb-4"><strong>5.4.</strong> O reembolso será devido apenas se, por culpa exclusiva da CONTRATADA, o certificado não for emitido em até 30 dias após o pagamento.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. RESPONSABILIDADES DO USUÁRIO</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>6.1.</strong> Declarar ser o autor ou titular legítimo da obra.</li>
                <li><strong>6.2.</strong> Assumir total responsabilidade civil e penal pelo conteúdo e informações fornecidas.</li>
                <li><strong>6.3.</strong> Manter cópias de segurança dos arquivos originais.</li>
                <li><strong>6.4.</strong> Não enviar obras de terceiros ou conteúdo ilícito.</li>
                <li><strong>6.5.</strong> Eximir a CONTRATADA de qualquer responsabilidade por litígios de autoria.</li>
                <li><strong>6.6.</strong> O USUÁRIO autoriza a MINHA COMPOSIÇÃO AUTORAL a atuar como sua procuradora exclusiva para fins de registro coletivo e periódico na Biblioteca Nacional, conforme previsto na cláusula 4.5, reconhecendo que a referida representação não implica cessão de direitos autorais.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. RESPONSABILIDADES DA CONTRATADA</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>7.1.</strong> Empregar as melhores práticas de segurança digital (LGPD e Marco Civil).</li>
                <li><strong>7.2.</strong> Emitir certificados contendo hash, carimbo de tempo e metadados.</li>
                <li><strong>7.3.</strong> Não se responsabilizar por falhas externas ou decisões judiciais sobre autoria.</li>
                <li><strong>7.4.</strong> O serviço é fornecido "no estado em que se encontra", sem garantia de êxito judicial.</li>
                <li><strong>7.5.</strong> A CONTRATADA compromete-se a: realizar o protocolo de registro coletivo na BN dentro dos prazos estipulados; anexar corretamente as obras registradas no período; repassar o número de registro a cada autor; garantir que o registro ocorra em nome do autor, constando a CONTRATADA apenas como procuradora administrativa.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. PROPRIEDADE INTELECTUAL DA PLATAFORMA</h2>
              <p className="text-gray-700 mb-4">
                Todo o conteúdo, software, layout e base de dados da plataforma são de titularidade exclusiva da MINHA COMPOSIÇÃO AUTORAL, protegidos pela legislação de propriedade intelectual. É proibida a reprodução sem autorização expressa.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. PRIVACIDADE E PROTEÇÃO DE DADOS</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>9.1.</strong> O tratamento de dados segue a Lei nº 13.709/18 (LGPD).</li>
                <li><strong>9.2.</strong> Medidas de segurança e criptografia são adotadas.</li>
                <li><strong>9.3.</strong> O USUÁRIO pode solicitar retificação ou exclusão de dados.</li>
                <li><strong>9.4.</strong> Dados e certificados podem ser mantidos para auditoria e defesa de direitos.</li>
                <li><strong>9.5.</strong> Os dados e obras poderão ser compartilhados com a Biblioteca Nacional exclusivamente para fins de registro oficial, respeitando-se a LGPD.</li>
                <li><strong>9.6.</strong> A Política de Privacidade integra este documento.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. LIMITAÇÃO DE RESPONSABILIDADE</h2>
              <p className="text-gray-700 mb-4">A CONTRATADA não garante:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>sucesso em disputas judiciais;</li>
                <li>prevenção absoluta de cópias;</li>
                <li>equivalência automática ao registro público.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Não se responsabiliza por danos indiretos, lucros cessantes ou perdas de dados.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. VIGÊNCIA, RESCISÃO E CANCELAMENTO</h2>
              <p className="text-gray-700 mb-2">Estes Termos têm vigência indeterminada.</p>
              <p className="text-gray-700 mb-2">O USUÁRIO pode encerrar sua conta a qualquer momento.</p>
              <p className="text-gray-700 mb-4">A CONTRATADA pode suspender contas em caso de violação legal ou contratual.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. COMUNICAÇÕES</h2>
              <p className="text-gray-700 mb-4">
                As comunicações oficiais serão feitas por e-mail cadastrado ou notificações internas da plataforma. Cabe ao USUÁRIO manter o endereço eletrônico atualizado.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. ALTERAÇÕES DOS TERMOS</h2>
              <p className="text-gray-700 mb-4">
                A CONTRATADA poderá atualizar estes Termos para refletir novas funcionalidades ou exigências legais. As alterações entram em vigor 15 dias após publicação, sendo o uso contínuo considerado aceitação.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. LEI APLICÁVEL E FORO</h2>
              <p className="text-gray-700 mb-4">
                Regidos pelas leis da República Federativa do Brasil, elegendo o Foro da Comarca de Lauro de Freitas – BA como competente para dirimir quaisquer controvérsias.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. DISPOSIÇÕES FINAIS</h2>
              <p className="text-gray-700 mb-2"><strong>15.1.</strong> A nulidade de uma cláusula não prejudica as demais.</p>
              <p className="text-gray-700 mb-2"><strong>15.2.</strong> Este documento constitui o acordo integral entre as partes.</p>
              <p className="text-gray-700 mb-4"><strong>15.3.</strong> A versão em português (Brasil) prevalece sobre quaisquer traduções.</p>
            </div>

            {/* Footer */}
            <div className="border-t pt-6 mt-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <p className="font-bold text-gray-900 mb-2">MINHA COMPOSIÇÃO AUTORAL</p>
                <p className="text-gray-700 mb-1">CNPJ: XXXXXX</p>
                <p className="text-gray-700 mb-1">Endereço: Avenida Santos Dumont, 6061, Sala 515, Portão</p>
                <p className="text-gray-700 mb-3">Lauro de Freitas – BA, CEP 42.712-740</p>
                <p className="text-gray-600 text-sm">© 2025 — Todos os direitos reservados</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
